import {
  JobCategoryWithCount,
  JobPositionWithCount,
  JobEducationLevelWithCount,
  JobEmploymentTypeWithCount,
  JobSalaryPeriod,
  JobCompanyWithCount,
} from './../models/job.model';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { formatDateLocalized } from '../utils/date-utils';
import { Observable, of, delay } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { SharedApiService, ApiType } from '../../../../../libs/shared/src/lib/services/api.service';
import { Job, JobDetailData, RecommendedJob, SavedJob } from '../models/job.model';
import { getJobEducationLevels } from '../../../../../libs/shared/src/lib/i18n/job-education-levels';

interface GetJobsResponse extends PaginatedResponse {
  content: RecommendedJob[];
}

interface JobDetailResponse {
  status: number;
  message: string;
  body: JobDetailData;
}

interface PaginatedResponse {
  empty: boolean;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pageable: {
    offset: number;
    page: number;
    size: number;
  };
  size: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  totalElements: number;
  totalPages: number;
}

interface RecommendedJobsResponse extends PaginatedResponse {
  content: RecommendedJob[];
}

interface SavedJobsResponse extends PaginatedResponse {
  content: SavedJob[];
}

interface SaveJobResponse {
  status: number;
  message: string;
  body?: any;
}

@Injectable({
  providedIn: 'root',
})
export class JobService {
  private readonly isServer: boolean;
  // Default fallback job shown when the API fails completely. A getter (not a
  // field) so it can read localized strings — translocoService isn't available
  // during field initialization.
  private get defaultJob(): Job {
    return {
      id: 0,
      title: this.translocoService.translate('job.detail.notFoundTitle'),
      tenantName: this.translocoService.translate('job.detail.unknownCompany'),
      tenantLogoUrl: '',
      location: [this.translocoService.translate('job.detail.unknownLocation')],
      experience: this.translocoService.translate('common.notSpecified'),
      jobType: ['Unknown'],
      type: ['Unknown'],
      salary: {
        basicSalary: 0,
        currency: '',
        period: '',
      },
      postedAt: new Date(Date.now()),
      education: this.translocoService.translate('common.notSpecified'),
      description: this.translocoService.translate('job.detail.loadFailedHtml'),
      requirements: [],
      benefits: [],
      jobCategory: {
        uuid: '',
        id: 0,
        categoryKey: 'Unknown',
        active: false,
      },
      validUntil: '',
      skills: [],
    };
  }

  constructor(private sharedApiService: SharedApiService, private translocoService: TranslocoService, @Inject(PLATFORM_ID) platformId: Object) {
    this.isServer = isPlatformServer(platformId);
  }

  /**
   * Get all jobs
   */
  getJobs(
    limit: number = 10,
    offset: number = 0,
    searchKeyword: string = '',
    searchLocation: string = '',
    categoryIDs: number[] = [],
    positionIDs: number[] = [],
    educationLevelIDs: number[] = [],
    employmentTypeIDs: number[] = [],
    companyIDs: number[] = [],
    salaryFilter: { minSalary: number; maxSalary: number; periodKey: string } = { minSalary: 0, maxSalary: 0, periodKey: 'month' },
    sortBy: string = 'newest',
    searchLatitude: number | null = null,
    searchLongitude: number | null = null,
    searchRadius: number | null = null
  ): Observable<{ jobs: RecommendedJob[]; total: number }> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('limit', limit.toString());
    queryParams = queryParams.append('offset', offset.toString());

    // Add sort parameter
    if (sortBy) {
      queryParams = queryParams.append('sortBy', sortBy);
    }

    // Add filter parameters
    if (categoryIDs.length > 0) {
      queryParams = queryParams.append('categoryIDs', JSON.stringify(categoryIDs.map(String)));
    }
    if (positionIDs.length > 0) {
      queryParams = queryParams.append('positionIDs', JSON.stringify(positionIDs.map(String)));
    }
    if (educationLevelIDs.length > 0) {
      queryParams = queryParams.append('educationLevelIDs', JSON.stringify(educationLevelIDs.map(String)));
    }
    if (employmentTypeIDs.length > 0) {
      queryParams = queryParams.append('employmentTypeIDs', JSON.stringify(employmentTypeIDs.map(String)));
    }
    if (companyIDs.length > 0) {
      queryParams = queryParams.append('companyIDs', JSON.stringify(companyIDs.map(String)));
    }

    // Add salary filter parameters (only if min or max salary is specified)
    if (salaryFilter.minSalary > 0 || salaryFilter.maxSalary > 0) {
      if (salaryFilter.minSalary > 0) {
        queryParams = queryParams.append('minSalary', salaryFilter.minSalary.toString());
      }
      if (salaryFilter.maxSalary > 0) {
        queryParams = queryParams.append('maxSalary', salaryFilter.maxSalary.toString());
      }
      // Only send period if we have a salary range
      if (salaryFilter.periodKey) {
        queryParams = queryParams.append('salaryPeriod', salaryFilter.periodKey);
      }
    }

    // Add search parameters
    if (searchKeyword) {
      queryParams = queryParams.append('filter', searchKeyword);
    }
    if (searchLocation) {
      queryParams = queryParams.append('filterLocation', searchLocation);
    }

    // Add location search parameters (lat/lng/radius)
    if (searchLatitude !== null && searchLongitude !== null) {
      queryParams = queryParams.append('searchLatitude', searchLatitude.toString());
      queryParams = queryParams.append('searchLongitude', searchLongitude.toString());
      if (searchRadius !== null) {
        queryParams = queryParams.append('searchRadius', searchRadius.toString());
      }
    }

    return this.sharedApiService.get<GetJobsResponse>('get-jobs', queryParams, { apiType: ApiType.DEFAULT }).pipe(
      map((response) => {
        // Return the content directly since it's already in the correct format
        return { jobs: response.content, total: response.totalElements };
      }),
      catchError((error) => {
        console.error('Error fetching jobs:', error);
        return of({ jobs: [], total: 0 });
      })
    );
  }

  /**
   * Get a single job by ID
   */
  getJobById(id: string): Observable<Job | undefined> {
    return this.sharedApiService.get<JobDetailResponse>(`get-job-offer-id-public?id=${id}`, undefined, { apiType: ApiType.DEFAULT }).pipe(
      map((response) => {
        if (response.status !== 200) {
          throw new Error(`Error fetching job: ${response.message}`);
        }

        const job = this.mapJobDetailToJob(response.body);
        return job;
      }),
      catchError((error) => {
        console.error(`Error fetching job with ID ${id}:`, error);
        return of(this.defaultJob);
      })
    );
  }

  /**
   * Translate education keys to human-readable text
   * Takes an array of education keys and returns a comma-separated string of translated values
   */
  private translateEducationKeys(educationKeys: string[] | undefined): string {
    if (!educationKeys || educationKeys.length === 0) {
      return 'Not specified';
    }

    // Get current language from TranslocoService
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const educationLevels = getJobEducationLevels(currentLang);

    const translatedValues = educationKeys
      .map((key) => {
        // Get translation from shared education levels
        const translated = educationLevels.jobEducationLevels[key as keyof typeof educationLevels.jobEducationLevels];

        // If translation not found, return formatted version of the key
        if (!translated) {
          // Convert snake_case to Title Case as fallback
          return key
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }

        return translated;
      })
      .filter((value) => value && value.length > 0);

    return translatedValues.length > 0 ? translatedValues.join(', ') : 'Not specified';
  }

  /**
   * Map job detail response to our Job model
   */
  private mapJobDetailToJob(jobDetail: JobDetailData): Job {
    // Use new array fields from backend, fall back to parsing old string fields
    let placeOfWork: string[] = [];
    let jobType: string[] = [];
    let jobBenefits: string[] = [];

    // Use jobTypeArr if available, otherwise try to parse jobType
    if (jobDetail.jobTypeArr && Array.isArray(jobDetail.jobTypeArr)) {
      jobType = jobDetail.jobTypeArr;
    } else if (typeof jobDetail.jobType === 'string') {
      try {
        jobType = JSON.parse(jobDetail.jobType);
      } catch (e) {
        jobType = [];
      }
    } else if (Array.isArray(jobDetail.jobType)) {
      jobType = jobDetail.jobType;
    }

    // Use placeOfWorkArr if available, otherwise try to parse placeOfWork
    if (jobDetail.placeOfWorkArr && Array.isArray(jobDetail.placeOfWorkArr)) {
      placeOfWork = jobDetail.placeOfWorkArr;
    } else if (typeof jobDetail.placeOfWork === 'string') {
      try {
        placeOfWork = JSON.parse(jobDetail.placeOfWork);
      } catch (e) {
        placeOfWork = [];
      }
    } else if (Array.isArray(jobDetail.placeOfWork)) {
      placeOfWork = jobDetail.placeOfWork;
    }

    // Use jobBenefitsArr if available, otherwise try to parse jobBenefits
    if (jobDetail.jobBenefitsArr && Array.isArray(jobDetail.jobBenefitsArr)) {
      jobBenefits = jobDetail.jobBenefitsArr;
    } else if (typeof jobDetail.jobBenefits === 'string') {
      try {
        jobBenefits = JSON.parse(jobDetail.jobBenefits);
      } catch (e) {
        jobBenefits = [];
      }
    } else if (Array.isArray(jobDetail.jobBenefits)) {
      jobBenefits = jobDetail.jobBenefits;
    }

    // Keep language requirements as structured objects for translation
    const languageRequirements = jobDetail.languagesJO?.map((lang) => ({ language: lang.language, level: lang.level })) || [];

    // Extract driver's license information
    const driverLicense = jobDetail.additionalInfo?.driversLicense?.join(', ') || '';

    // Translate education keys to readable text
    const educationText = this.translateEducationKeys(jobDetail.education?.school);

    return {
      id: jobDetail.id,
      title: jobDetail.jobTitle,
      tenantName: jobDetail.tenant?.name,
      tenantLogoUrl: jobDetail.tenant?.logo || '',
      tenantAddresses: jobDetail.tenant?.tenantAddresses || [],
      location: placeOfWork,
      experience: jobDetail.additionalInfo?.practicePosition || 'Not specified',
      type: jobType,
      jobType: jobType,
      salary: jobDetail.salary,
      postedAt: jobDetail.postedAt,
      education: educationText,
      description: jobDetail.jobDescription || '<p>No description provided</p>',
      requirements: [
        // Extract education requirements
        ...(educationText && educationText !== 'Not specified' ? [`Education: ${educationText}`] : []),
        // Extract language requirements
        ...(jobDetail.languagesJO?.map((lang) => `${lang.language}: ${lang.level}`) || []),
        // Extract driver's license if available
        ...(jobDetail.additionalInfo?.driversLicense?.length ? [`Driver's license: ${jobDetail.additionalInfo.driversLicense.join(', ')}`] : []),
      ],
      benefits: jobBenefits,
      jobCategory: jobDetail.jobCategory,
      validUntil: '',
      skills: jobDetail.skillsJO?.map((skill) => ({ skillName: skill.skillName, level: skill.level })) || [],
      summaries: jobDetail.summaries || [],

      // New fields for essential information section
      startDate: jobDetail.startDate ? formatDateLocalized(jobDetail.startDate, this.translocoService.getActiveLang()) : '',
      startDateAsap: jobDetail.startDateAsap || false,
      placeOfWork: placeOfWork,
      languageRequirements: languageRequirements,
      driverLicense: driverLicense,

      // Include the additionalInfo object for requirements section
      additionalInfo: jobDetail.additionalInfo
        ? {
            graduate: jobDetail.additionalInfo.graduate || false,
            practicePosition: jobDetail.additionalInfo.practicePosition || '',
            driversLicense: jobDetail.additionalInfo.driversLicense || [],
            practiceYear: jobDetail.additionalInfo.practiceYear || 0,
            additionalPersonalSkills: jobDetail.additionalInfo.additionalPersonalSkills || '',
          }
        : undefined,

      // User-specific flags
      isSaved: jobDetail.isSaved ?? false,
      isApplied: jobDetail.isApplied ?? false,
    };
  }

  /**
   * Get applied jobs for the logged-in user using v2 endpoint
   */
  getAppliedJobs(limit: number = 10, page: number = 0): Observable<{ jobs: RecommendedJob[]; totalElements: number }> {
    // If running on server, return mock data
    if (this.isServer) {
      return of({ jobs: [], totalElements: 0 }).pipe(delay(100));
    }

    interface AppliedJobsResponse extends PaginatedResponse {
      content: any[];
    }

    let queryParams = new HttpParams();
    // If limit is -1, don't send limit parameter to get all results
    if (limit !== -1) {
      queryParams = queryParams.append('limit', limit.toString());
    }
    // Add page parameter
    queryParams = queryParams.append('page', page.toString());

    return this.sharedApiService
      .get<AppliedJobsResponse>('api/user/applied-jobs', queryParams, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // Extract content from paginated response
          const jobs = response.content || [];
          // Return the applied jobs data, parsing JSON arrays if needed
          const mappedJobs = jobs.map((job) => {
            // Parse string arrays from backend if they exist
            if (typeof job.placeOfWork === 'string') {
              try {
                job.placeOfWork = JSON.parse(job.placeOfWork);
              } catch (e) {
                job.placeOfWork = [];
              }
            }
            if (typeof job.jobType === 'string') {
              try {
                job.jobType = JSON.parse(job.jobType);
              } catch (e) {
                job.jobType = [];
              }
            }
            if (typeof job.jobBenefits === 'string') {
              try {
                job.jobBenefits = JSON.parse(job.jobBenefits);
              } catch (e) {
                job.jobBenefits = [];
              }
            }

            // Map to RecommendedJob format
            return {
              ...job,
              jobTitle: job.jobTitle,
              jobDescription: job.jobDescription,
              jobCreatedAt: job.postedAt || new Date().toISOString(),
              postedAt: new Date(job.postedAt || new Date()),
              // Applied jobs specific fields stored as additional properties
              applicationStatus: job.applicationStatus,
              appliedDate: job.appliedDate,
            } as RecommendedJob;
          });

          return {
            jobs: mappedJobs,
            totalElements: response.totalElements || 0,
          };
        }),
        catchError((error) => {
          console.error('Error fetching applied jobs:', error);
          return of({ jobs: [], totalElements: 0 });
        })
      );
  }

  /**
   * Get recommended jobs for the logged-in candidate
   */
  getRecommendedJobs(limit: number = 10): Observable<RecommendedJob[]> {
    // If running on server, return empty array (requires auth)
    if (this.isServer) {
      return of([]);
    }

    let queryParams = new HttpParams();
    queryParams = queryParams.append('limit', limit.toString());

    return this.sharedApiService
      .get<RecommendedJobsResponse>('api/user/recommended-jobs-for-candidate', queryParams, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // Return the recommended jobs data
          return response.content || [];
        }),
        catchError((error) => {
          console.error('Error fetching recommended jobs:', error);
          return of([]);
        })
      );
  }

  /**
   * Save a job to the user's saved jobs list
   */
  saveJob(jobId: string): Observable<SaveJobResponse> {
    // If running on server, return mock data
    if (this.isServer) {
      return of({ status: 200, message: 'OK' }).pipe(delay(100));
    }

    return this.sharedApiService
      .post<any>(`api/user/saved-jobs/${jobId}`, {}, undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // API returns "OK" on success
          return { status: 200, message: response || 'OK' };
        }),
        catchError((error) => {
          console.error(`Error saving job with ID ${jobId}:`, error);
          // Don't handle error here - let component handle it
          throw error;
        })
      );
  }

  /**
   * Unsave a job from the user's saved jobs list
   */
  unsaveJob(jobId: string): Observable<SaveJobResponse> {
    // If running on server, return mock data
    if (this.isServer) {
      return of({ status: 200, message: 'OK' }).pipe(delay(100));
    }

    return this.sharedApiService
      .delete<any>(`api/user/saved-jobs/${jobId}`, undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // API returns "OK" on success
          return { status: 200, message: response || 'OK' };
        }),
        catchError((error) => {
          console.error(`Error unsaving job with ID ${jobId}:`, error);
          return of({ status: error.status || 500, message: error.message || 'Failed to unsave job' });
        })
      );
  }

  /**
   * Get saved jobs for the logged-in user with pagination
   */
  getSavedJobs(page: number = 0, size: number = 10): Observable<{ jobs: Job[]; total: number }> {
    // If running on server, return mock data
    if (this.isServer) {
      return of({ jobs: [], total: 0 }).pipe(delay(100));
    }

    let queryParams = new HttpParams();
    queryParams = queryParams.append('page', page.toString());
    // If size is -1, don't send limit parameter to get all results
    if (size !== -1) {
      queryParams = queryParams.append('limit', size.toString());
    }

    return this.sharedApiService
      .get<SavedJobsResponse>('api/user/saved-jobs', queryParams, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          const mappedJobs = this.mapSavedJobsToJobs(response.content);
          return {
            jobs: mappedJobs,
            total: response.totalElements,
          };
        }),
        catchError((error) => {
          console.error('Error fetching saved jobs:', error);
          return of({ jobs: [], total: 0 });
        })
      );
  }

  /**
   * Map saved jobs to our Job model
   */
  private mapSavedJobsToJobs(savedJobs: SavedJob[]): Job[] {
    if (!savedJobs || !Array.isArray(savedJobs)) {
      return [];
    }
    return savedJobs.map((savedJob) => this.mapSavedJobToJob(savedJob));
  }

  /**
   * Map a single saved job to our Job model
   */
  private mapSavedJobToJob(savedJob: SavedJob): Job {
    const jobInfo = savedJob.job;

    return {
      id: jobInfo.id,
      title: jobInfo.jobTitle,
      tenantName: jobInfo.tenantName,
      tenantLogoUrl: jobInfo.tenantLogoUrl,
      location: jobInfo.placeOfWork,
      experience: 'WHAT IS THIS?',
      type: jobInfo.jobType,
      jobType: jobInfo.jobType,
      salary: jobInfo.salary,
      postedAt: new Date(jobInfo.jobCreatedAt),
      education: '',
      description: jobInfo.jobDescription || '',
      requirements: [],
      benefits: jobInfo.jobBenefits || [],
      jobCategory: jobInfo.jobCategory,
      jobPosition: jobInfo.jobPosition,
      validUntil: '',
      skills: [],
      isSaved: true, // Since this comes from saved jobs, it's always saved
      matchScore: jobInfo.matchScore,
    };
  }

  /**
   * Get job categories with job counts
   */
  getJobCategoriesWithCounts(): Observable<JobCategoryWithCount[]> {
    // If running on server, return mock data
    if (this.isServer) {
      return of([]).pipe(delay(100));
    }

    return this.sharedApiService
      .get<JobCategoryWithCount[]>('api/get-categories-counts', undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => response || []),
        catchError((error) => {
          console.error('Error fetching job categories with counts:', error);
          return of([]);
        })
      );
  }

  /**
   * Get job positions with job counts
   */
  getJobPositionsWithCounts(): Observable<JobPositionWithCount[]> {
    // If running on server, return mock data
    if (this.isServer) {
      return of([]).pipe(delay(100));
    }

    return this.sharedApiService
      .get<JobPositionWithCount[]>('api/get-positions-counts', undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => response || []),
        catchError((error) => {
          console.error('Error fetching job positions with counts:', error);
          return of([]);
        })
      );
  }

  /**
   * Get job filters (categories, positions, education levels, employment types) with counts based on current filter selection
   */
  getJobFilters(
    categoryIDs: number[] = [],
    positionIDs: number[] = [],
    educationLevelIDs: number[] = [],
    employmentTypeIDs: number[] = [],
    salaryFilter: { minSalary: number; maxSalary: number; periodKey: string } = { minSalary: 0, maxSalary: 0, periodKey: 'month' }
  ): Observable<{
    categories: JobCategoryWithCount[];
    positions: JobPositionWithCount[];
    educationLevels: JobEducationLevelWithCount[];
    employmentTypes: JobEmploymentTypeWithCount[];
    salaryPeriods: JobSalaryPeriod[];
    companies: JobCompanyWithCount[];
  }> {
    // If running on server, return mock data
    if (this.isServer) {
      return of({ categories: [], positions: [], educationLevels: [], employmentTypes: [], salaryPeriods: [], companies: [] }).pipe(delay(100));
    }

    let queryParams = new HttpParams();
    if (categoryIDs.length > 0) {
      queryParams = queryParams.append('categoryIDs', categoryIDs.join(','));
    }
    if (positionIDs.length > 0) {
      queryParams = queryParams.append('positionIDs', positionIDs.join(','));
    }
    if (educationLevelIDs.length > 0) {
      queryParams = queryParams.append('educationLevelIDs', educationLevelIDs.join(','));
    }
    if (employmentTypeIDs.length > 0) {
      queryParams = queryParams.append('employmentTypeIDs', employmentTypeIDs.join(','));
    }
    // Add salary filter parameters only if min or max salary is specified
    if (salaryFilter.minSalary > 0 || salaryFilter.maxSalary > 0) {
      if (salaryFilter.minSalary > 0) {
        queryParams = queryParams.append('minSalary', salaryFilter.minSalary.toString());
      }
      if (salaryFilter.maxSalary > 0) {
        queryParams = queryParams.append('maxSalary', salaryFilter.maxSalary.toString());
      }
      // Only send period if we have a salary range
      if (salaryFilter.periodKey) {
        queryParams = queryParams.append('salaryPeriod', salaryFilter.periodKey);
      }
    }

    return this.sharedApiService
      .get<{
        categories: JobCategoryWithCount[];
        positions: JobPositionWithCount[];
        educationLevels: JobEducationLevelWithCount[];
        employmentTypes: JobEmploymentTypeWithCount[];
        salaryPeriods: JobSalaryPeriod[];
        companies: JobCompanyWithCount[];
      }>('api/get-filters', queryParams, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => response || { categories: [], positions: [], educationLevels: [], employmentTypes: [], salaryPeriods: [], companies: [] }),
        catchError((error) => {
          console.error('Error fetching job filters:', error);
          return of({ categories: [], positions: [], educationLevels: [], employmentTypes: [], salaryPeriods: [], companies: [] });
        })
      );
  }
}
